import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { publicAPI, privateAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import PostnummerFilter from '../components/PostnummerFilter'
import DateFilterBar, { OutputMode, getDateRangeForMode } from '../components/DateFilterBar'
import { useYearDates } from '../contexts/YearContext'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import { OwnershipChart } from '../components/Charts'
import { PieChart, Car, Building2 } from 'lucide-react'
import PeriodInfoBanner from '../components/PeriodInfoBanner'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useMineMerker } from '../hooks/useMineMerker'
import { localizeApiDetail } from '../i18n/apiError'

// Helper function to safely parse numeric values that might be strings
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

interface CompanyCarBrandData {
  brand: string
  total_company_cars: number
  owned_count: number
  owned_percentage: number
  leased_count: number
  leased_percentage: number
  private_leasing_count: number
  private_leasing_percentage: number
  company_leasing_count: number
  company_leasing_percentage: number
  ev_count: number
  ev_percentage: number
}

interface CompanyCarModelData {
  brand: string
  master_model: string
  total_company_cars: number
  owned_count: number
  owned_percentage: number
  leased_count: number
  leased_percentage: number
  private_leasing_count: number
  private_leasing_percentage: number
  company_leasing_count: number
  company_leasing_percentage: number
  ev_count: number
  ev_percentage: number
}

export default function CompanyAnalytics() {
  const { t, i18n } = useTranslation('leasingLoans')
  // Period label for the banner; mirrors DateFilterBar's OUTPUT_MODE_LABELS.
  // The persisted mode can be one DateFilterBar doesn't know (e.g. last_week from Aldersanalyse).
  const periodLabel = (mode: OutputMode) =>
    i18n.exists(`leasingLoans:outputModes.${mode}`) ? t(`outputModes.${mode}` as const) : t('periodFallback')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'brands' | 'models'>('overview')
  const [ownershipStats, setOwnershipStats] = useState<any>(null)
  const [brandData, setBrandData] = useState<CompanyCarBrandData[]>([])
  const [modelData, setModelData] = useState<CompanyCarModelData[]>([])
  const [brandsLoading, setBrandsLoading] = useState(false)
  const [modelsLoading, setModelsLoading] = useState(false)

  // Date range state from year context
  const { startDate, setStartDate, endDate, setEndDate, selectedYear, maxSvvDate } = useYearDates('company-analytics')

  // DateFilterBar state
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')

  // Vehicle filter
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()

  // Max data date for banner
  const maxDataDate = useMaxDataDate()

  // Postal code filter state
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('company-analytics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)

  // Mine merker highlighting
  const { activeMerker } = useMineMerker()

  // Date range validation
  const [dateRangeAlert, setDateRangeAlert] = useState<string | null>(null)

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchOwnershipData()
    } else if (activeTab === 'brands') {
      fetchBrandData()
    } else if (activeTab === 'models') {
      fetchModelData()
    }
  }, [activeTab, selectedYear, vehicleFilter])

  // Validate date range (max 366 days)
  const validateDateRange = (start: Date, end: Date): boolean => {
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays > 366) {
      setDateRangeAlert(t('company.dateRangeTooLarge', { days: diffDays }))
      return false
    }

    setDateRangeAlert(null)
    return true
  }

  const fetchActiveTab = () => {
    if (activeTab === 'overview') fetchOwnershipData()
    else if (activeTab === 'brands') fetchBrandData()
    else if (activeTab === 'models') fetchModelData()
  }

  const handleApplyFilters = () => {
    if (outputMode === 'custom') {
      if (customStartDate && customEndDate) {
        const s = new Date(customStartDate)
        const e = new Date(customEndDate)
        if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && validateDateRange(s, e)) {
          setStartDate(s)
          setEndDate(e)
          setTimeout(() => fetchActiveTab(), 0)
        }
      }
    } else {
      const { startDate: sd, endDate: ed } = getDateRangeForMode(outputMode, maxSvvDate ?? undefined)
      setStartDate(new Date(sd))
      setEndDate(new Date(ed))
      setDateRangeAlert(null)
      setTimeout(() => fetchActiveTab(), 0)
    }
  }

  const handleOutputModeChange = (mode: OutputMode) => {
    setOutputMode(mode)
    if (mode !== 'custom') {
      const { startDate: sd, endDate: ed } = getDateRangeForMode(mode, maxSvvDate ?? undefined)
      setStartDate(new Date(sd))
      setEndDate(new Date(ed))
      setDateRangeAlert(null)
      setTimeout(() => fetchActiveTab(), 0)
    }
  }


  const fetchOwnershipData = async () => {
    try {
      setLoading(true)
      setError(null)

      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date }

      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      const response = await publicAPI.getOwnershipStatistics(params)
      setOwnershipStats(response.data.data)
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('company.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const fetchBrandData = async () => {
    try {
      setBrandsLoading(true)
      setError(null)

      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date, limit: 50 }

      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const response = await privateAPI.getCompanyCarsByBrand(params)
      setBrandData(response.data.data || [])
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('company.loadBrandsError'))
    } finally {
      setBrandsLoading(false)
    }
  }

  const fetchModelData = async () => {
    try {
      setModelsLoading(true)
      setError(null)

      const start_date = startDate.toISOString().split('T')[0]
      const end_date = endDate.toISOString().split('T')[0]

      const varebilParam = getVarebilParam()
      const params: any = { start_date, end_date, limit: 50 }

      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const response = await privateAPI.getCompanyCarsByMasterModel(params)
      setModelData(response.data.data || [])
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('company.loadModelsError'))
    } finally {
      setModelsLoading(false)
    }
  }

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('company.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('company.subtitle')}
        </p>
      </div>



      {/* Date Range Filter */}
      <DateFilterBar
        outputMode={outputMode}
        onOutputModeChange={handleOutputModeChange}
        customStartDate={customStartDate}
        onCustomStartDateChange={setCustomStartDate}
        customEndDate={customEndDate}
        onCustomEndDateChange={setCustomEndDate}
        onApply={handleApplyFilters}
        bottomContent={
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        }
      />

      {/* Date Range Alert */}
      {dateRangeAlert && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>{t('company.errorPrefix')}</strong> {dateRangeAlert}
          </p>
        </div>
      )}

      {/* Period Info Banner */}
      <PeriodInfoBanner
        maxDataDate={maxDataDate}
        outputMode={outputMode}
        periodLabel={periodLabel(outputMode)}
        startDate={startDate.toISOString().split('T')[0]}
        endDate={endDate.toISOString().split('T')[0]}
      />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-dark-border">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'overview'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <PieChart className="w-4 h-4" />
            {t('company.tabs.overview')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('brands')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'brands'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {t('company.tabs.brands')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('models')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'models'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Car className="w-4 h-4" />
            {t('company.tabs.models')}
          </span>
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('company.kpi.totalRegistrations')}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {ownershipStats?.total_registrations?.toLocaleString('nb-NO')}
          </p>
        </div>
        <div className="card p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('company.kpi.privateOwned')}</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {ownershipStats?.private_owned_share?.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('company.kpi.cars', { n: ownershipStats?.private_owned_count?.toLocaleString('nb-NO') ?? '' })}
          </p>
        </div>
        <div className="card p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('company.kpi.companyOwned')}</p>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {ownershipStats?.company_owned_share?.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('company.kpi.cars', { n: ownershipStats?.company_owned_count?.toLocaleString('nb-NO') ?? '' })}
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {t('company.ownershipTitle')}
          </h2>
          {ownershipStats && (
            <OwnershipChart
              privateCount={ownershipStats.private_owned_count}
              companyCount={ownershipStats.company_owned_count}
            />
          )}
        </div>

        <div className="card p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {t('company.detailsTitle')}
          </h2>
          <div className="space-y-4">
            <div className="border-b border-gray-200 dark:border-dark-border pb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('company.period')}</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">
                {t('company.periodRange', { start: ownershipStats?.start_date ?? '', end: ownershipStats?.end_date ?? '' })}
              </p>
            </div>
            <div className="border-b border-gray-200 dark:border-dark-border pb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('company.privateOwnedCars')}</p>
              <p className="text-lg font-semibold text-green-600 dark:text-green-400 mt-1">
                {ownershipStats?.private_owned_count?.toLocaleString('nb-NO')} ({ownershipStats?.private_owned_share?.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %)
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('company.companyOwnedCars')}</p>
              <p className="text-lg font-semibold text-orange-600 dark:text-orange-400 mt-1">
                {ownershipStats?.company_owned_count?.toLocaleString('nb-NO')} ({ownershipStats?.company_owned_share?.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %)
              </p>
            </div>
          </div>
        </div>
      </div>
        </>
      )}

      {/* Brands Tab */}
      {activeTab === 'brands' && (
        <>
          {brandsLoading ? (
            <div className="card p-8 text-center">
              <LoadingSpinner />
            </div>
          ) : brandData.length === 0 ? (
            <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
              {t('company.noDataApply')}
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="text-center w-12">#</th>
                    <th>{t('company.cols.brand')}</th>
                    <th className="text-right">{t('company.cols.companyCars')}</th>
                    <th className="text-right">{t('company.cols.owned')}</th>
                    <th className="text-right">{t('company.cols.leased')}</th>
                  </tr>
                </thead>
                <tbody>
                  {brandData.map((brand, index) => (
                    <tr key={`${brand.brand}-${index}`} className={activeMerker.has(brand.brand) ? 'bg-primary-100 dark:bg-primary-900/30 border-l-4 border-primary-500' : ''}>
                      <td className="text-center text-gray-700 dark:text-gray-300">{index + 1}</td>
                      <td className="font-semibold">{brand.brand}</td>
                      <td className="text-right">{brand.total_company_cars.toLocaleString('nb-NO')}</td>
                      <td className="text-right">
                        {toNumber(brand.owned_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({brand.owned_count.toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(brand.leased_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({brand.leased_count.toLocaleString('nb-NO')})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Models Tab */}
      {activeTab === 'models' && (
        <>
          {modelsLoading ? (
            <div className="card p-8 text-center">
              <LoadingSpinner />
            </div>
          ) : modelData.length === 0 ? (
            <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
              {t('company.noDataApply')}
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="text-center w-12">#</th>
                    <th>{t('company.cols.brand')}</th>
                    <th>{t('company.cols.model')}</th>
                    <th className="text-right">{t('company.cols.companyCars')}</th>
                    <th className="text-right">{t('company.cols.owned')}</th>
                    <th className="text-right">{t('company.cols.leased')}</th>
                  </tr>
                </thead>
                <tbody>
                  {modelData.map((model, index) => (
                    <tr key={`${model.brand}-${model.master_model}-${index}`} className={activeMerker.has(model.brand) ? 'bg-primary-100 dark:bg-primary-900/30 border-l-4 border-primary-500' : ''}>
                      <td className="text-center text-gray-700 dark:text-gray-300">{index + 1}</td>
                      <td className="font-semibold">{model.brand}</td>
                      <td>{model.master_model}</td>
                      <td className="text-right">{model.total_company_cars.toLocaleString('nb-NO')}</td>
                      <td className="text-right">
                        {toNumber(model.owned_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({model.owned_count.toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(model.leased_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({model.leased_count.toLocaleString('nb-NO')})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
